import { values, uniq } from "lodash";
import * as moment from "moment";

import * as actions from "./actionTypes";
import { QuestionnaireState, initialState, Section, QuestionMap, QuestionnairesState } from "./model";
import {
    QuestionnaireAction,
    UpdateQuestion,
    RemoveQuestion,
    UserRemoveQuestion,
    ToggleSection,
    OpenSection,
    NextSection,
    ValidateQuestionComplete
} from "./actions";
import { isSectionVisible } from "./utils";
import { AUTO_SAVE_REFERRAL_FORM_SUCCESS } from "./components/referralForm/actionTypes";
import { AUTO_SAVE_HEALTH_SURVEILLANCE_REVIEW_SUCCESS } from "./components/healthSurveillance/review/actionTypes";
import { AUTO_SAVE_IMMUNISATION_REVIEW_SUCCESS } from "./components/immunisationReview/actionTypes";

export function reducer(state: QuestionnairesState = initialState, action: QuestionnaireAction): QuestionnairesState {
    switch (action.type) {
        case actions.QUESTIONNAIRE_LOADED:
        case actions.UPDATE_QUESTION:
        case actions.VALIDATE_QUESTION_COMPLETE:
        case actions.REGISTER_SECTION:
        case actions.REGISTER_SECTION_INDEX_IF_NEEDED:
        case actions.REMOVE_QUESTION:
        case actions.USER_REMOVE_QUESTION:
        case actions.TOGGLE_SECTION:
        case actions.OPEN_SECTION:
        case actions.NEXT_SECTION:
        case actions.SUBMIT_QUESTIONNAIRE_SUCCESS:
        case actions.SUBMIT_INVALID_QUESTIONNAIRE:
        case actions.CLEAR_QUESTIONNAIRE:
        case actions.RESET_ACTIVE_SECTIONS:
        case actions.SAVE_QUESTIONNAIRE_SUCCESS:
        case AUTO_SAVE_REFERRAL_FORM_SUCCESS:
        case AUTO_SAVE_HEALTH_SURVEILLANCE_REVIEW_SUCCESS:
        case AUTO_SAVE_IMMUNISATION_REVIEW_SUCCESS:
        case actions.REMOVE_SECTION: {
            return {
                ...state,
                [action.payload.questionnaireKey]: questionnaireReducer(state[action.payload.questionnaireKey], action)
            };
        }
        default:
            return state;
    }
}

function questionnaireReducer(state: QuestionnaireState = { sections: [] }, action: QuestionnaireAction): QuestionnaireState {

    switch (action.type) {
        case actions.QUESTIONNAIRE_LOADED: {
            const referralSubmittedDate = action.payload.referralSubmittedDate && moment(action.payload.referralSubmittedDate);
            const loadedQuestions = action.payload.questions;
            const existingSectionIds = state.sections.map(s => s.id);
            const incomingSectionIds = uniq(action.payload.questions.map(q => q.sectionId));
            const newSections: Section[] = incomingSectionIds
                .filter(newSectionId => existingSectionIds.indexOf(newSectionId) === -1)
                .map(sectionId => ({
                    id: sectionId,
                    title: sectionId,
                    questions: {},
                    active: false,
                    isValid: true,
                    isComplete: false,
                    neverHide: false
                }));
            return {
                ...state,
                sections: [ ...newSections, ...state.sections].map(section => {
                    const questions = loadedQuestions.filter(q => q.sectionId === section.id).reduce<QuestionMap>((map, q) => {
                        map[q.id] = q;
                        return map;
                    }, {});
                    return {
                        ...section,
                        isComplete: false,
                        active: false,
                        questions,
                        isValid: isSectionValid(questions)
                    };
                }),
                eTag: action.payload.eTag,
                referralSubmittedDate,
                isDefaultCustomerType: action.payload.isDefaultCustomerType,
                customerType: action.payload.customerType,
                reportChangesBlocked: action.payload.reportChangesBlocked,
                releaseReportComments: action.payload.releaseReportComments,
                approvalState: action.payload.approvalState,
                isConsentWithdrawn: action.payload.isConsentWithdrawn
            };
        }
        case AUTO_SAVE_REFERRAL_FORM_SUCCESS: {
            const loadedQuestions = action.payload.questions;
            const existingSectionIds = state.sections.map(s => s.id);
            const incomingSectionIds = uniq(action.payload.questions.map(q => q.sectionId));
            const newSections: Section[] = incomingSectionIds
                .filter(newSectionId => existingSectionIds.indexOf(newSectionId) === -1)
                .map(sectionId => ({
                    id: sectionId,
                    title: sectionId,
                    questions: {},
                    active: false,
                    isValid: true,
                    isComplete: false,
                    neverHide: false
                }));
            return {
                ...state,
                // The Auto save is sometimes used for loading the initial state of the form, in these cases isInitial = true
                // This is the only time we want to set the sections via this reducer, otherwise we ignore them as the user may have updated via the UI
                // and we do not want to override that
                sections: action.payload.isInitial ? [...state.sections, ...newSections].map(section => {
                    const questions = loadedQuestions.filter(q => q.sectionId === section.id).reduce<QuestionMap>((map, q) => {
                        map[q.id] = q;
                        return map;
                    }, {});
                    return {
                        ...section,
                        questions,
                        isValid: isSectionValid(questions)
                    };
                }) : state.sections,
                eTag: action.payload.eTag
            };
        }
        case AUTO_SAVE_HEALTH_SURVEILLANCE_REVIEW_SUCCESS: {
            return {
                ...state,
                eTag: action.payload.eTag
            };
        }
        case AUTO_SAVE_IMMUNISATION_REVIEW_SUCCESS: {
            return {
                ...state,
                eTag: action.payload.eTag
            };
        }
        case actions.UPDATE_QUESTION: {
            const { id, sectionId, question, title } = action.payload;
            question.sectionId = sectionId;
            if (state.sections.some(s => s.id === sectionId)) {
                return mapSections(state, action);
            }
            const newSection: Section = {
                id: sectionId,
                title: title ?? sectionId,
                questions: { [id]: { id, ...question } },
                active: state.sections.length === 0,
                isValid: question.isValid,
                isComplete: false,
                neverHide: false
            };
            return { ...state, sections: [...state.sections, newSection] };
        }
        case actions.VALIDATE_QUESTION_COMPLETE: {
            const { id, sectionId, question } = action.payload;
            if (state.sections.find(s => s.id === sectionId)?.questions[id].answer === question.answer) {
                question.sectionId = sectionId;
                return mapSections(state, action);
            }
            return state;
        }
        case actions.REGISTER_SECTION_INDEX_IF_NEEDED: {
            let section = state.sections.find(s => s.id === action.payload.sectionId);

            if (section && section.index !== action.payload.index) {
                section = { ...section, index: action.payload.index };
                const sections = state.sections.filter(s => s.id !== action.payload.sectionId);
                const indexesLowerThanSectionIndex = sections.map(s => s.index).filter(i => (i || i === 0) && i < action.payload.index);
                if (indexesLowerThanSectionIndex.length > 0) {
                    const maxIndex = Math.max(...indexesLowerThanSectionIndex);
                    const maxIndexSectionIndex = sections.findIndex(s => s.index === maxIndex);
                    return {
                        ...state, sections: [
                            ...sections.slice(0, maxIndexSectionIndex + 1),
                            section,
                            ...sections.slice(maxIndexSectionIndex + 1)
                        ]
                    };
                } else {
                    return {
                        ...state, sections: [
                            ...sections,
                            section
                        ]
                    };
                }
            }

            return state;
        }
        case actions.REGISTER_SECTION: {
            const { sectionId, active, title, neverHide } = action.payload;
            let section = state.sections.find(s => s.id === action.payload.sectionId);
            if (!section) {
                section = {
                    id: sectionId,
                    index: action.payload.index,
                    questions: {},
                    active,
                    title,
                    isValid: true,
                    isComplete: false,
                    neverHide
                };
            } else {
                section.index = action.payload.index;
                section.title = title;
                section.neverHide = neverHide;
            }

            const sectionIndex = state.sections.findIndex(s => s.id === action.payload.sectionId);
            const sections = state.sections.filter(s => s.id !== action.payload.sectionId);
            if (action.payload.index !== undefined) {
                const indexesLowerThanSectionIndex = sections.map(s => s.index).filter(i => (i || i === 0) && i < action.payload.index);
                if (indexesLowerThanSectionIndex.length > 0) {
                    const maxIndex = Math.max(...indexesLowerThanSectionIndex);
                    const maxIndexSectionIndex = sections.findIndex(s => s.index === maxIndex);
                    return {
                        ...state, sections: [
                            ...sections.slice(0, maxIndexSectionIndex + 1),
                            section,
                            ...sections.slice(maxIndexSectionIndex + 1)
                        ]
                    };
                } else {
                    return {
                        ...state, sections: [
                            section,
                            ...sections,
                        ]
                    };
                }
            } else if (sectionIndex !== -1) {
                return {
                    ...state, sections: [
                        ...sections.slice(0, sectionIndex),
                        section,
                        ...sections.slice(sectionIndex)
                    ]
                };
            } else {
                return {
                    ...state, sections: [
                        ...sections,
                        section
                    ]
                };
            }
        }
        case actions.REMOVE_QUESTION:
        case actions.USER_REMOVE_QUESTION:
        case actions.TOGGLE_SECTION:
        case actions.OPEN_SECTION: {
            return mapSections(state, action);
        }
        case actions.NEXT_SECTION: {
            const sectionIndex = state.sections.findIndex(sections => sections.id === action.payload.sectionId);
            const nextSection = state.sections.find((s, index) =>
                s.id !== undefined && index > sectionIndex && isSectionVisible(s)
            );
            return mapSections(state, { ...action, payload: { ...action.payload, nextSectionId: nextSection && nextSection.id } });
        }
        case actions.SUBMIT_QUESTIONNAIRE_SUCCESS:
            return { ...state, sections: [] };
        case actions.SUBMIT_INVALID_QUESTIONNAIRE:
            let firstInactiveSection: Section;
            if (action.payload.openFirstInvalid) {
                firstInactiveSection = state.sections.find(s => !s.isValid);
            }
            return { ...state, sections: state.sections.map(s => ({ ...s, active: s === firstInactiveSection, isComplete: true })) };
        case actions.CLEAR_QUESTIONNAIRE:
            return { ...state, sections: [], eTag: null };
        case actions.RESET_ACTIVE_SECTIONS:
            return { ...state, sections: state.sections.map((s, i) => ({ ...s, active: i === 0 })) };
        case actions.SAVE_QUESTIONNAIRE_SUCCESS:
            return { ...state, eTag: action.payload.eTag };
        case actions.REMOVE_SECTION:
            return { ...state, sections: state.sections.filter(s => s.id !== action.payload.sectionId) };
        default:
            return state;
    }
}

function mapSections(
    state: QuestionnaireState,
    action: UpdateQuestion | RemoveQuestion | UserRemoveQuestion | ToggleSection | OpenSection | NextSection | ValidateQuestionComplete) {
    if (action.type === actions.OPEN_SECTION && !state.sections.some(s => s.id === action.payload.sectionId)) {
        return {
            ...state,
            sections: state.sections.concat({
                active: true,
                id: action.payload.sectionId,
                questions: {},
                isValid: true,
                isComplete: false,
                title: action.payload.sectionId,
                neverHide: false
            })
        };
    }
    return {
        ...state,
        sections: state.sections.map(section => sectionReducer(section, action))
    };
}

function sectionReducer(
    state: Section,
    action: UpdateQuestion | RemoveQuestion | UserRemoveQuestion | ToggleSection | OpenSection | NextSection | ValidateQuestionComplete): Section {
    if (action.type === actions.NEXT_SECTION) {
        return {
            ...state,
            active: action.payload.nextSectionId === state.id,
            isComplete: state.isComplete || action.payload.sectionId === state.id
        };
    }

    if (state.id !== action.payload.sectionId) {
        if (action.type === actions.OPEN_SECTION && action.payload.closeOthers) {
            return { ...state, active: false };
        }
        return state;
    }

    switch (action.type) {
        case actions.UPDATE_QUESTION:
        case actions.VALIDATE_QUESTION_COMPLETE: {
            const { id, question } = action.payload;
            const sectionQuestions = { ...state.questions };
            const existingQuestion = sectionQuestions[id];
            const isRemovedByUser = existingQuestion?.isRemovedByUser;
            const isValid = isRemovedByUser ? true : question?.isValid;
            sectionQuestions[id] = { id, ...question, isRemovedByUser, isValid };
            return { ...state, questions: sectionQuestions, isValid: isSectionValid(sectionQuestions) };
        }
        case actions.REMOVE_QUESTION: {
            const { id } = action.payload;
            const questions = { ...state.questions };
            delete questions[id];
            return { ...state, questions, isValid: isSectionValid(questions) };
        }
        case actions.USER_REMOVE_QUESTION: {
            const { id } = action.payload;
            const sectionQuestions = { ...state.questions };
            sectionQuestions[id] = { ...sectionQuestions[id], isRemovedByUser: true, answer: null, isValid: true };
            return { ...state, questions: sectionQuestions, isValid: isSectionValid(sectionQuestions) };
        }
        case actions.TOGGLE_SECTION: {
            return { ...state, active: !state.active };
        }
        case actions.OPEN_SECTION: {
            return { ...state, active: true };
        }
        default:
            return state;
    }
}

function isSectionValid(questionMap: QuestionMap) {
    const questions = values(questionMap);
    if (questions.every(q => q.isValid === true)) {
        return true;
    } else if (questions.some(q => !q.isRemovedByUser && q.isValid === false)) {
        return false;
    }
    return null;
}
